import React, { useState, useRef } from "react";
import _ from "lodash";
import {
  Button,
  VARIANT,
  COLOR,
} from "@metacrm/metacrm-material-ui/dist/Button";
import {
  StyledGeneralSettingsContainer,
  StyledSettingTitle,
  StyledDescription,
  StyledSettingContent,
  StyledSettingContentLabel,
  StyledSettingContentValue,
  StyledChainDataContainer,
  StyledTokenRow,
  StyledTokenList,
} from "../OnChainDataSource.styles";
import Checkbox from "@metacrm/metacrm-material-ui/dist/Checkbox";
import { BasicDialog } from "@metacrm/metacrm-material-ui/dist/Dialog";
import { PopperMenu } from "@metacrm/metacrm-material-ui/dist/PopperMenu";
import { apiUrl } from "features/configure";
import axios from "axios";
import Box from "@mui/material/Box";
import { useIntl } from "react-intl";
import { Controller, useFormContext } from "react-hook-form";

const BlockchainNetworkPreference = ({
  entityName,
  ecosystemName,
  canEdit,
  chainList,
}) => {
  const { formatMessage } = useIntl();

  const anchorRef = useRef(null);
  const [tokenList, setTokenList] = useState("");
  const [openChainPopperMenu, setOpenChainPopperMenu] = useState(false);

  const { control, setValue, watch } = useFormContext();
  const blockchainNetworks = watch("blockchainNetworks") || [];

  const handleChangeBlockchainNetworks = (chainId, checked) => {
    const index = blockchainNetworks.findIndex(
      (item) => item.chainId === chainId
    );

    let updated = [];

    // checked / unchecked
    if (index > -1) {
      updated = blockchainNetworks.map((item, i) =>
        i === index ? { ...item, checked } : item
      );
    } else {
      // add new chain
      updated = [...blockchainNetworks, { chainId, checked }];
    }

    // make sure at least one chain is selected
    const checkedCount = updated.filter((c) => c.checked).length;
    if (checkedCount === 0) {
      return blockchainNetworks;
    }

    return updated;
  };

  const unAddedChains = chainList.filter(
    (chain) =>
      !blockchainNetworks.some(
        (selectedChain) => selectedChain.chainId === chain.id
      )
  );

  return (
    <StyledGeneralSettingsContainer>
      <StyledSettingTitle>
        {formatMessage({
          id: "dataSourceSetting.blockchainNetworks.title",
        })}
      </StyledSettingTitle>
      <StyledDescription>
        {formatMessage({
          id: "dataSourceSetting.blockchainNetworks.description",
        })}
      </StyledDescription>

      <StyledSettingContent>
        <StyledSettingContentLabel>
          {formatMessage({ id: "dataSourceSetting.headline.chain" })}
        </StyledSettingContentLabel>
        <StyledSettingContentValue>
          {_.map(blockchainNetworks, (chain, i) => {
            const isChecked = chain.checked;
            const chainInfo = chainList.find(
              (info) => info.id === chain.chainId
            );

            if (!chainInfo) return;

            return (
              <StyledChainDataContainer selected={isChecked} key={i}>
                {canEdit && (
                  <Controller
                    name={`blockchainNetworks`}
                    control={control}
                    render={({ field }) => (
                      <>
                        <Checkbox
                          checked={isChecked}
                          onChange={(e) => {
                            const updated = handleChangeBlockchainNetworks(
                              ecosystemName.toLowerCase() === "evm"
                                ? Number(e.target.value)
                                : e.target.value,
                              e.target.checked
                            );
                            // only update if value is different
                            if (updated !== blockchainNetworks) {
                              field.onChange(updated);
                            }
                          }}
                          value={chainInfo?.value}
                          style={{ marginRight: 12 }}
                          checkboxSize={14}
                        />
                      </>
                    )}
                  />
                )}
                {chainInfo.label}
                <Button
                  variant={VARIANT.TEXT}
                  color={COLOR.SECONDARY}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    position: "absolute",
                    right: 5,
                  }}
                  onClick={async (e) => {
                    const result = await axios.get(
                      apiUrl +
                        `/api/dataSource/${entityName}/assetList?chainId=${chainInfo.value}&assetType=token`
                    );
                    setTokenList({
                      chainId: chainInfo.value,
                      tokens: result.data,
                    });
                  }}
                >
                  <span style={{ fontWeight: 700 }}>
                    {formatMessage({
                      id: "global.btn.tokenList",
                    })}
                  </span>
                </Button>
              </StyledChainDataContainer>
            );
          })}
          {canEdit && unAddedChains.length > 0 && (
            <StyledChainDataContainer
              addButton
              onClick={() => setOpenChainPopperMenu(!openChainPopperMenu)}
              ref={anchorRef}
            >
              <i
                className="meta-crm-icon-ic_add font-size-14"
                style={{ paddingRight: 12 }}
              />
              {formatMessage({ id: "global.btn.addAnotherChain" })}
              <PopperMenu
                itemList={_.map(unAddedChains, (chain, i) => {
                  return {
                    children: <>{chain.label}</>,
                    func: (e) => {
                      const updated = handleChangeBlockchainNetworks(
                        chain.value,
                        false
                      );
                      setValue("blockchainNetworks", updated);
                    },
                  };
                })}
                placement="bottom-end"
                open={openChainPopperMenu}
                onClose={() => setOpenChainPopperMenu(false)}
                anchorEl={anchorRef.current}
                paperStyle={{ width: anchorRef.current?.offsetWidth }}
              />
            </StyledChainDataContainer>
          )}
        </StyledSettingContentValue>
      </StyledSettingContent>

      <BasicDialog
        open={!_.isEmpty(tokenList)}
        onClose={() => {
          setTokenList("");
        }}
        dialogRootStyle={{ maxWidth: "420px" }}
      >
        <Box
          sx={{
            width: "400px",
            padding: "24px",
            display: !_.isEmpty(tokenList) ? "flex" : "none",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{ fontWeight: "700", fontSize: "18px", marginBottom: "12px" }}
          >
            {formatMessage({
              id: "global.list.token",
            })}
          </Box>
          <Box
            color={(theme) => theme.customColors.grey[600]}
            sx={{ marginBottom: "24px" }}
          >
            <div className="startRow">
              {formatMessage({ id: "dataSourceSetting.headline.chain" })} :{" "}
              {_.find(chainList, { value: _.get(tokenList, "chainId") })?.label}
            </div>
          </Box>
          <StyledTokenList>
            <Box>
              {_.map(_.get(tokenList, "tokens", []), (token, i) => {
                return (
                  <StyledTokenRow key={i}>
                    <img
                      className="smIcon"
                      src={token.icon}
                      style={{ marginRight: 8, borderRadius: 24 }}
                    />
                    {token.name}
                  </StyledTokenRow>
                );
              })}
            </Box>
          </StyledTokenList>
        </Box>
      </BasicDialog>
    </StyledGeneralSettingsContainer>
  );
};

export default BlockchainNetworkPreference;
